import { useEffect, useState } from "react";
import { MessageCircle, Mail, X } from "lucide-react";
import { BRAND, useI18n } from "@/i18n";
import { trackEvent } from "@/lib/analytics";

export default function FloatingActions() {
  const { t } = useI18n();
  const [openWa, setOpenWa] = useState(false);
  const [openMail, setOpenMail] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const greeting = t.floating.greeting;
  const waUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(greeting)}`;
  const mailUrl = `mailto:${BRAND.email}?subject=${encodeURIComponent(t.floating.emailSubject)}&body=${encodeURIComponent(t.floating.emailBody)}`;

  const toggleWa = () => { setOpenWa((v) => !v); setOpenMail(false); };
  const toggleMail = () => { setOpenMail((v) => !v); setOpenWa(false); };

  return (
    <div
      className={`fixed bottom-20 sm:bottom-24 right-5 sm:right-7 z-40 flex flex-col items-end gap-3 transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      {/* --- Email tooltip / button --- */}
      <div className="relative">
        {openMail && (
          <div
            className="absolute bottom-[60px] right-0 w-72 bg-[#FAF9F6] border border-[#E5E0D8] shadow-2xl rounded-sm p-5"
            data-testid="email-tooltip"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="overline text-[#C85A3C]">Email</p>
                <p className="font-serif-display text-xl text-[#2C2520] leading-tight mt-1">
                  {t.floating.question}
                </p>
              </div>
              <button
                onClick={() => setOpenMail(false)}
                className="text-[#5C564F] hover:text-[#2C2520] -mt-1 -mr-1 p-1"
                aria-label="close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-[#5C564F] mt-2 leading-relaxed">
              {t.floating.emailSub}
            </p>
            <a
              href={mailUrl}
              data-testid="email-cta"
              onClick={() => trackEvent("contact_email", { channel: "floating_button" })}
              className="mt-4 block bg-[#C85A3C] hover:bg-[#A6472D] text-white text-center py-3 rounded-sm tracking-widest uppercase text-xs font-medium transition"
            >
              {t.floating.emailCta}
            </a>
            <p className="text-[10px] text-[#5C564F]/60 text-center mt-2">{BRAND.email}</p>
          </div>
        )}

        <button
          onClick={toggleMail}
          data-testid="email-floating-btn"
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#C85A3C] hover:bg-[#A6472D] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
          aria-label="Email"
        >
          {openMail ? <X className="w-5 h-5" /> : <Mail className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.8} />}
        </button>
      </div>

      {/* --- WhatsApp tooltip / button --- */}
      <div className="relative">
        {openWa && (
          <div
            className="absolute bottom-[72px] right-0 w-72 bg-[#FAF9F6] border border-[#E5E0D8] shadow-2xl rounded-sm p-5"
            data-testid="whatsapp-tooltip"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="overline text-[#25D366]">WhatsApp</p>
                <p className="font-serif-display text-xl text-[#2C2520] leading-tight mt-1">
                  {t.floating.question}
                </p>
              </div>
              <button
                onClick={() => setOpenWa(false)}
                className="text-[#5C564F] hover:text-[#2C2520] -mt-1 -mr-1 p-1"
                aria-label="close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-[#5C564F] mt-2 leading-relaxed">
              {t.floating.sub}
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-cta"
              onClick={() => trackEvent("contact_whatsapp", { channel: "floating_button" })}
              className="mt-4 block bg-[#25D366] hover:bg-[#1FAD52] text-white text-center py-3 rounded-sm tracking-widest uppercase text-xs font-medium transition"
            >
              {t.floating.cta}
            </a>
            <p className="text-[10px] text-[#5C564F]/60 text-center mt-2">{BRAND.phone}</p>
          </div>
        )}

        <button
          onClick={toggleWa}
          data-testid="whatsapp-floating-btn"
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#25D366] hover:bg-[#1FAD52] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105 relative"
          aria-label="WhatsApp"
        >
          {openWa ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={1.8} />}
          {!openWa && (
            <span className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
}
